/**
 * AudioSystem.js
 *
 * Phase 10 — Pure WebAudio procedural sound for the KRAKEN game.
 *
 * Why procedural?  No asset hosting needed and every browser ships with
 * WebAudio API, so we get fire/mine/ambient SFX without bundling any
 * binary files. Each effect is a short oscillator/noise burst routed
 * through a gain → biquad filter → master gain → destination chain.
 *
 * Volume hierarchy:
 *   destination
 *     ← masterGain  (user "master volume")
 *         ← sfxGain (laser/mine/UI clicks)
 *         ← ambientGain (low-level wind / hum loop)
 *
 * The audio context is lazily created on the first user-gesture-driven
 * `play*()` call to comply with browser autoplay policies.
 *
 * Public API:
 *   const audio = new AudioSystem();
 *   audio.setMasterVolume(0..1)
 *   audio.setSfxVolume(0..1)
 *   audio.setAmbientVolume(0..1)
 *   audio.playLaser()
 *   audio.playMine()
 *   audio.playUi()
 *   audio.playMineExtract()
 *   audio.startAmbient()
 *   audio.stopAmbient()
 *   audio.dispose()
 */

export class AudioSystem {
  constructor(initial = {}) {
    this._ctx = null;
    this._master = null;
    this._sfx = null;
    this._ambientGain = null;
    this._ambientNodes = [];
    this._ambientRunning = false;
    this._volumes = {
      master: clamp01(initial.master ?? 0.8),
      sfx: clamp01(initial.sfx ?? 0.8),
      ambient: clamp01(initial.ambient ?? 0.6),
    };
  }

  /** Lazy-create the AudioContext + gain graph (browser autoplay safe). */
  _ensureCtx() {
    if (this._ctx) {
      // Resume if the page got suspended (e.g. tab returned to foreground)
      if (this._ctx.state === 'suspended') {
        this._ctx.resume().catch(() => {});
      }
      return;
    }
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    this._ctx = new AC();
    this._master = this._ctx.createGain();
    this._master.gain.value = this._volumes.master;
    this._master.connect(this._ctx.destination);

    this._sfx = this._ctx.createGain();
    this._sfx.gain.value = this._volumes.sfx;
    this._sfx.connect(this._master);

    this._ambientGain = this._ctx.createGain();
    this._ambientGain.gain.value = this._volumes.ambient;
    this._ambientGain.connect(this._master);
  }

  // ---------- Volume API ----------

  setMasterVolume(v) {
    this._volumes.master = clamp01(v);
    if (this._master) this._master.gain.value = this._volumes.master;
  }
  setSfxVolume(v) {
    this._volumes.sfx = clamp01(v);
    if (this._sfx) this._sfx.gain.value = this._volumes.sfx;
  }
  setAmbientVolume(v) {
    this._volumes.ambient = clamp01(v);
    if (this._ambientGain) this._ambientGain.gain.value = this._volumes.ambient;
  }
  setVolumes(v = {}) {
    if (typeof v.master === 'number') this.setMasterVolume(v.master);
    if (typeof v.sfx === 'number') this.setSfxVolume(v.sfx);
    if (typeof v.ambient === 'number') this.setAmbientVolume(v.ambient);
  }
  getVolumes() {
    return { ...this._volumes };
  }

  // ---------- One-shot SFX ----------

  /** Short "pew" laser shot — descending oscillator with sharp attack. */
  playLaser() {
    this._ensureCtx();
    if (!this._ctx) return;
    const now = this._ctx.currentTime;
    const dur = 0.18;
    const osc = this._ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.setValueAtTime(880, now);
    osc.frequency.exponentialRampToValueAtTime(220, now + dur);

    const gain = this._ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.35, now + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);

    const bp = this._ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1400;
    bp.Q.value = 0.7;

    osc.connect(gain).connect(bp).connect(this._sfx);
    osc.start(now);
    osc.stop(now + dur + 0.02);
  }

  /** Mining drill — looped low rumble while RMB is held. */
  playMine() {
    // We don't loop here — caller fires this periodically (e.g. every 0.18s)
    // so the cost is bounded. For continuous drone, see startMineLoop below.
    this._ensureCtx();
    if (!this._ctx) return;
    const now = this._ctx.currentTime;
    const dur = 0.2;

    const noise = this._createNoiseBuffer(0.4);
    const src = this._ctx.createBufferSource();
    src.buffer = noise;

    const lp = this._ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 380;
    lp.Q.value = 0.4;

    const gain = this._ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.20, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);

    src.connect(lp).connect(gain).connect(this._sfx);
    src.start(now);
    src.stop(now + dur + 0.05);
  }

  /** Extracted-unit confirmation — bright "ping". */
  playMineExtract() {
    this._ensureCtx();
    if (!this._ctx) return;
    const now = this._ctx.currentTime;
    const dur = 0.32;
    const osc = this._ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(660, now);
    osc.frequency.exponentialRampToValueAtTime(1320, now + dur);

    const gain = this._ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.28, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);

    osc.connect(gain).connect(this._sfx);
    osc.start(now);
    osc.stop(now + dur + 0.02);
  }

  /** Subtle UI click for buttons/tabs. */
  playUi() {
    this._ensureCtx();
    if (!this._ctx) return;
    const now = this._ctx.currentTime;
    const dur = 0.08;
    const osc = this._ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(1200, now);
    osc.frequency.exponentialRampToValueAtTime(900, now + dur);

    const gain = this._ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.12, now + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);

    osc.connect(gain).connect(this._sfx);
    osc.start(now);
    osc.stop(now + dur + 0.02);
  }

  /** Damage taken — gritty thud. */
  playHurt() {
    this._ensureCtx();
    if (!this._ctx) return;
    const now = this._ctx.currentTime;
    const dur = 0.22;
    const noise = this._createNoiseBuffer(0.4);
    const src = this._ctx.createBufferSource();
    src.buffer = noise;

    const lp = this._ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 240;
    lp.Q.value = 0.4;

    const gain = this._ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.45, now + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);

    src.connect(lp).connect(gain).connect(this._sfx);
    src.start(now);
    src.stop(now + dur + 0.05);
  }

  /** Thunder — a sharp crack opening into a low, decaying rumble + sub boom. */
  playThunder(strength = 1) {
    this._ensureCtx();
    if (!this._ctx) return;
    const ctx = this._ctx;
    const now = ctx.currentTime;
    const s = Math.max(0.25, Math.min(1, strength));
    const dur = 1.6 + s * 1.6;

    const noise = this._createNoiseBuffer(dur + 0.2);
    const src = ctx.createBufferSource();
    src.buffer = noise;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(1900, now);
    lp.frequency.exponentialRampToValueAtTime(150, now + 0.5);
    lp.Q.value = 0.6;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.55 * s, now + 0.02);  // crack
    gain.gain.exponentialRampToValueAtTime(0.24 * s, now + 0.28);  // rumble
    gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    src.connect(lp).connect(gain).connect(this._sfx);

    const boom = ctx.createOscillator();
    boom.type = 'sine';
    boom.frequency.setValueAtTime(72, now);
    boom.frequency.exponentialRampToValueAtTime(36, now + dur * 0.7);
    const bg = ctx.createGain();
    bg.gain.setValueAtTime(0.0001, now);
    bg.gain.exponentialRampToValueAtTime(0.34 * s, now + 0.05);
    bg.gain.exponentialRampToValueAtTime(0.0001, now + dur * 0.85);
    boom.connect(bg).connect(this._sfx);

    src.start(now); src.stop(now + dur + 0.1);
    boom.start(now); boom.stop(now + dur + 0.1);
  }


  // ---------- Ambient drone (low wind/hum loop) ----------

  startAmbient() {
    this._ensureCtx();
    if (!this._ctx || this._ambientRunning) return;
    this._ambientRunning = true;
    const ctx = this._ctx;

    // Two slow detuned sine waves + filtered noise = soft "wind + hum" bed.
    const a = ctx.createOscillator();
    a.type = 'sine';
    a.frequency.value = 55;
    const b = ctx.createOscillator();
    b.type = 'sine';
    b.frequency.value = 82.4;

    const noiseSrc = ctx.createBufferSource();
    noiseSrc.buffer = this._createNoiseBuffer(2.0, true);
    noiseSrc.loop = true;

    const lpN = ctx.createBiquadFilter();
    lpN.type = 'lowpass';
    lpN.frequency.value = 280;
    lpN.Q.value = 0.2;

    const blend = ctx.createGain();
    blend.gain.value = 0.5;

    const noiseGain = ctx.createGain();
    noiseGain.gain.value = 0.35;

    a.connect(blend);
    b.connect(blend);
    noiseSrc.connect(lpN).connect(noiseGain).connect(blend);

    const ambient = ctx.createGain();
    ambient.gain.setValueAtTime(0.0001, ctx.currentTime);
    ambient.gain.exponentialRampToValueAtTime(0.20, ctx.currentTime + 1.2);

    blend.connect(ambient).connect(this._ambientGain);

    a.start();
    b.start();
    noiseSrc.start();
    this._ambientNodes = [a, b, noiseSrc, ambient];
  }

  stopAmbient() {
    if (!this._ambientRunning) return;
    this._ambientRunning = false;
    const ctx = this._ctx;
    if (!ctx) return;
    const now = ctx.currentTime;
    for (const node of this._ambientNodes) {
      try {
        if (node.gain) {
          node.gain.cancelScheduledValues(now);
          node.gain.setValueAtTime(node.gain.value, now);
          node.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);
        }
      } catch (_e) { /* noop */ }
    }
    setTimeout(() => {
      for (const node of this._ambientNodes) {
        try { node.stop?.(); } catch (_e) { /* noop */ }
        try { node.disconnect?.(); } catch (_e) { /* noop */ }
      }
      this._ambientNodes = [];
    }, 500);
  }

  // ---------- Cave ambience (deep echoing drone + occasional drips) ----------

  startCaveAmbient() {
    this._ensureCtx();
    if (!this._ctx || this._caveRunning) return;
    this._caveRunning = true;
    const ctx = this._ctx;

    // Low subterranean drone (two very low detuned sines).
    const a = ctx.createOscillator();
    a.type = 'sine';
    a.frequency.value = 38;
    const b = ctx.createOscillator();
    b.type = 'sine';
    b.frequency.value = 45.7;

    // Heavily low-passed noise = distant cave rumble.
    const noiseSrc = ctx.createBufferSource();
    noiseSrc.buffer = this._createNoiseBuffer(3.0, true);
    noiseSrc.loop = true;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 160;
    lp.Q.value = 0.4;
    const noiseGain = ctx.createGain();
    noiseGain.gain.value = 0.5;

    const blend = ctx.createGain();
    blend.gain.value = 0.45;
    a.connect(blend);
    b.connect(blend);
    noiseSrc.connect(lp).connect(noiseGain).connect(blend);

    const caveGain = ctx.createGain();
    caveGain.gain.setValueAtTime(0.0001, ctx.currentTime);
    caveGain.gain.exponentialRampToValueAtTime(0.22, ctx.currentTime + 1.5);
    blend.connect(caveGain).connect(this._ambientGain);

    a.start();
    b.start();
    noiseSrc.start();

    // Periodic water "drip" pings for atmosphere.
    this._caveDripTimer = setInterval(() => {
      if (!this._caveRunning || !this._ctx) return;
      try {
        const c2 = this._ctx;
        const o = c2.createOscillator();
        o.type = 'sine';
        const f = 700 + Math.random() * 900;
        o.frequency.setValueAtTime(f, c2.currentTime);
        o.frequency.exponentialRampToValueAtTime(f * 0.55, c2.currentTime + 0.18);
        const g = c2.createGain();
        g.gain.setValueAtTime(0.0001, c2.currentTime);
        g.gain.exponentialRampToValueAtTime(0.12, c2.currentTime + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, c2.currentTime + 0.35);
        o.connect(g).connect(this._ambientGain);
        o.start();
        o.stop(c2.currentTime + 0.4);
      } catch (_e) { /* noop */ }
    }, 2200 + Math.random() * 2600);

    this._caveNodes = [a, b, noiseSrc, caveGain];
  }

  stopCaveAmbient() {
    if (!this._caveRunning) return;
    this._caveRunning = false;
    if (this._caveDripTimer) { clearInterval(this._caveDripTimer); this._caveDripTimer = null; }
    const ctx = this._ctx;
    if (!ctx) return;
    const now = ctx.currentTime;
    for (const node of this._caveNodes || []) {
      try {
        if (node.gain) {
          node.gain.cancelScheduledValues(now);
          node.gain.setValueAtTime(node.gain.value, now);
          node.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);
        }
      } catch (_e) { /* noop */ }
    }
    const nodes = this._caveNodes || [];
    setTimeout(() => {
      for (const node of nodes) {
        try { node.stop?.(); } catch (_e) { /* noop */ }
        try { node.disconnect?.(); } catch (_e) { /* noop */ }
      }
    }, 500);
    this._caveNodes = [];
  }

  // ---------- Internal helpers ----------

  _createNoiseBuffer(seconds = 0.4, isStereo = false) {
    const ctx = this._ctx;
    const channels = isStereo ? 2 : 1;
    const buf = ctx.createBuffer(channels, Math.floor(ctx.sampleRate * seconds), ctx.sampleRate);
    for (let c = 0; c < channels; c++) {
      const data = buf.getChannelData(c);
      for (let i = 0; i < data.length; i++) {
        data[i] = (Math.random() * 2 - 1) * 0.85;
      }
    }
    return buf;
  }

  dispose() {
    this.stopAmbient();
    try { this._master?.disconnect?.(); } catch (_e) { /* noop */ }
    try { this._sfx?.disconnect?.(); } catch (_e) { /* noop */ }
    try { this._ambientGain?.disconnect?.(); } catch (_e) { /* noop */ }
    try { this._ctx?.close?.(); } catch (_e) { /* noop */ }
    this._ctx = null;
  }
}

function clamp01(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

// Singleton (most consumers want one shared audio system instance)
let _singleton = null;
export function getAudioSystem() {
  if (!_singleton) _singleton = new AudioSystem();
  return _singleton;
}

export default AudioSystem;
